import React, { FC, useEffect, useRef } from 'react';
import type { OrganisationSeatPlan } from '@dreamai/types';
import { ReactComponent as CloseIcon } from '../../icons/close-modal.svg';
import { ReactComponent as CheckCircleIcon } from '../../icons/check-circle.svg';
import { ReactComponent as DreamMarque } from '../../icons/brand/dream-marque.svg';
import { formatMinor } from './organisationCreditLabels';
import './subscription-gate-modal.scss';

const FEATURES = [
  'An AI Secretary for every member',
  'Travel and expenses for your organisation',
  'Enjoy unlimited secretary usage',
];

interface SubscriptionGateModalProps {
  /** Price of one member seat, once billing has loaded */
  seatPlan: OrganisationSeatPlan | null;
  purpose?: 'invite' | 'team_assistant';
  busy: boolean;
  error?: string | null;
  onSubscribe: () => void;
  onClose: () => void;
}

/** Shown in place of the invite form until the organisation can pay for seats (Figma "Members / Only You"). */
const SubscriptionGateModal: FC<SubscriptionGateModalProps> = ({
  seatPlan,
  purpose = 'invite',
  busy,
  error,
  onSubscribe,
  onClose,
}) => {
  const latest = useRef({ busy, onClose });
  latest.current = { busy, onClose };
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || latest.current.busy) return;
      e.stopPropagation();
      latest.current.onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const close = () => {
    if (!busy) onClose();
  };

  return (
    <div
      className='organisation-modal-overlay'
      onClick={close}
      data-testid='subscription-gate-modal'
    >
      <div
        className='subscription-gate'
        role='dialog'
        aria-modal='true'
        aria-labelledby='subscription-gate-title'
        onClick={(e) => e.stopPropagation()}
      >
        <header className='subscription-gate-hero'>
          <span className='subscription-gate-logo' aria-hidden='true'>
            <DreamMarque />
          </span>
          <h2 className='subscription-gate-title' id='subscription-gate-title'>
            {purpose === 'team_assistant'
              ? 'Set up a subscription to add Reve'
              : 'Setup a subscription to invite your team'}
          </h2>
          <p className='subscription-gate-lede'>
            Manage all your organisation&rsquo;s secretaries in one place.
          </p>
          <button
            type='button'
            className='subscription-gate-close'
            aria-label='Close'
            disabled={busy}
            onClick={close}
          >
            <CloseIcon />
          </button>
        </header>

        <div className='subscription-gate-body'>
          <div className='subscription-gate-plan'>
            {seatPlan && (
              <div
                className='subscription-gate-price'
                data-testid='subscription-gate-price'
              >
                <span className='subscription-gate-amount'>
                  {formatMinor(seatPlan.unitAmountMinor, seatPlan.currency)}
                  <small>/mo</small>
                </span>
                <span className='subscription-gate-unit'>per member + VAT</span>
              </div>
            )}
            <ul className='subscription-gate-features'>
              {FEATURES.map((feature) => (
                <li key={feature}>
                  <CheckCircleIcon aria-hidden='true' />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          <p className='subscription-gate-note'>
            Your secretary will be moved to a subscription when you choose
            &lsquo;set up&rsquo; below.
            {purpose === 'invite' && (
              <>
                <br />
                Anyone you invite will only be billed if they accept your
                invitation.
              </>
            )}
          </p>
          {error && (
            <p className='subscription-gate-error' role='alert'>
              {error}
            </p>
          )}
        </div>

        <footer className='subscription-gate-actions'>
          <button
            type='button'
            className='subscription-gate-cancel'
            disabled={busy}
            onClick={close}
          >
            Cancel
          </button>
          <button
            type='button'
            className='subscription-gate-primary'
            disabled={busy}
            onClick={onSubscribe}
          >
            {busy ? 'Opening…' : 'Set up subscription'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default SubscriptionGateModal;
